/**
 * EmailWindow Component
 *
 * A styled email window frame with hybrid macOS/Windows aesthetic.
 * Features:
 * - macOS-style traffic light buttons
 * - Professional email header layout (From, To, CC, Subject, Date)
 * - Clean, modern design with rounded corners
 * - Scrollable body content area
 */

import { ReactNode } from 'react';
import { Paperclip, Calendar, User, Users, Mail } from 'lucide-react';

interface EmailWindowProps {
  subject: string;
  fromHtml: string;
  toHtml: string;
  ccHtml?: string | null;
  date: string;
  attachments?: string[];
  children: ReactNode;
}

export function EmailWindow({
  subject,
  fromHtml,
  toHtml,
  ccHtml,
  date,
  attachments = [],
  children,
}: EmailWindowProps) {
  return (
    <div className="email-window">
      {/* Window Title Bar */}
      <div className="email-window-titlebar">
        <div className="email-window-traffic-lights">
          <div className="traffic-light traffic-light-close" title="Close" />
          <div className="traffic-light traffic-light-minimize" title="Minimize" />
          <div className="traffic-light traffic-light-maximize" title="Maximize" />
        </div>
        <div className="email-window-title">
          <Mail className="w-4 h-4 opacity-60" />
          <span className="truncate">{subject}</span>
        </div>
        <div className="email-window-titlebar-spacer" />
      </div>

      {/* Email Header */}
      <div className="email-header">
        {/* From */}
        <div className="email-header-row">
          <div className="email-header-label">
            <User className="w-4 h-4" />
            <span>From</span>
          </div>
          <div
            className="email-header-value email-from"
            dangerouslySetInnerHTML={{ __html: fromHtml }}
          />
        </div>

        {/* To */}
        <div className="email-header-row">
          <div className="email-header-label">
            <Users className="w-4 h-4" />
            <span>To</span>
          </div>
          <div
            className="email-header-value"
            dangerouslySetInnerHTML={{ __html: toHtml }}
          />
        </div>

        {/* CC (if present) */}
        {ccHtml && (
          <div className="email-header-row">
            <div className="email-header-label">
              <Users className="w-4 h-4" />
              <span>CC</span>
            </div>
            <div
              className="email-header-value"
              dangerouslySetInnerHTML={{ __html: ccHtml }}
            />
          </div>
        )}

        {/* Date */}
        <div className="email-header-row">
          <div className="email-header-label">
            <Calendar className="w-4 h-4" />
            <span>Date</span>
          </div>
          <div className="email-header-value">
            {date}
          </div>
        </div>

        {/* Subject */}
        <div className="email-header-row email-subject-row">
          <div className="email-header-label">
            <Mail className="w-4 h-4" />
            <span>Subject</span>
          </div>
          <div className="email-header-value email-subject">
            {subject}
          </div>
        </div>

        {/* Attachments (if any) */}
        {attachments.length > 0 && (
          <div className="email-attachments">
            <div className="email-header-label">
              <Paperclip className="w-4 h-4" />
              <span>Attachments</span>
            </div>
            <div className="email-attachments-list">
              {attachments.map((attachment, index) => (
                <div key={index} className="email-attachment-chip">
                  <Paperclip className="w-3 h-3" />
                  <span>{attachment}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Email Body */}
      <div className="email-body">
        {children}
      </div>

      {/* Styles */}
      <style>{`
        .email-window {
          background: #ffffff;
          border-radius: 12px;
          box-shadow:
            0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -2px rgba(0, 0, 0, 0.1),
            0 0 0 1px rgba(0, 0, 0, 0.05);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        /* Title Bar */
        .email-window-titlebar {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          background: linear-gradient(180deg, #f9fafb 0%, #f3f4f6 100%);
          border-bottom: 1px solid #e5e7eb;
          gap: 12px;
        }

        .email-window-traffic-lights {
          display: flex;
          gap: 8px;
          flex-shrink: 0;
        }

        .traffic-light {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          cursor: default;
          transition: filter 0.15s ease;
        }

        .traffic-light:hover {
          filter: brightness(0.9);
        }

        .traffic-light-close {
          background: linear-gradient(180deg, #ff5f57 0%, #e0443e 100%);
          border: 1px solid #e0443e;
        }

        .traffic-light-minimize {
          background: linear-gradient(180deg, #febc2e 0%, #d9a123 100%);
          border: 1px solid #d9a123;
        }

        .traffic-light-maximize {
          background: linear-gradient(180deg, #28c840 0%, #1fa834 100%);
          border: 1px solid #1fa834;
        }

        .email-window-title {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 500;
          color: #4b5563;
          min-width: 0;
        }

        .email-window-titlebar-spacer {
          width: 52px; /* Balance the traffic lights */
          flex-shrink: 0;
        }

        /* Email Header */
        .email-header {
          padding: 16px 20px;
          background: #fafafa;
          border-bottom: 1px solid #e5e7eb;
        }

        .email-header-row {
          display: flex;
          align-items: flex-start;
          padding: 6px 0;
          font-size: 14px;
        }

        .email-header-label {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 80px;
          flex-shrink: 0;
          color: #6b7280;
          font-weight: 500;
        }

        .email-header-value {
          flex: 1;
          color: #1f2937;
          word-break: break-word;
        }

        .email-from {
          font-weight: 600;
        }

        .email-subject-row {
          margin-top: 8px;
          padding-top: 12px;
          border-top: 1px solid #e5e7eb;
        }

        .email-subject {
          font-size: 16px;
          font-weight: 600;
          color: #111827;
        }

        /* Attachments */
        .email-attachments {
          display: flex;
          align-items: flex-start;
          padding-top: 12px;
          margin-top: 8px;
          border-top: 1px solid #e5e7eb;
          font-size: 14px;
        }

        .email-attachments-list {
          flex: 1;
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .email-attachment-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 12px;
          color: #4b5563;
        }

        .email-attachment-chip:hover {
          background: #e5e7eb;
        }

        /* Email Body */
        .email-body {
          flex: 1;
          padding: 24px;
          overflow: auto;
          background: #ffffff;
          min-height: 200px;
        }

        /* Dark mode support */
        @media (prefers-color-scheme: dark) {
          .email-window {
            background: #1f2937;
            box-shadow:
              0 4px 6px -1px rgba(0, 0, 0, 0.3),
              0 2px 4px -2px rgba(0, 0, 0, 0.2),
              0 0 0 1px rgba(255, 255, 255, 0.05);
          }

          .email-window-titlebar {
            background: linear-gradient(180deg, #374151 0%, #1f2937 100%);
            border-bottom-color: #374151;
          }

          .email-window-title {
            color: #d1d5db;
          }

          .email-header {
            background: #111827;
            border-bottom-color: #374151;
          }

          .email-header-label {
            color: #9ca3af;
          }

          .email-header-value {
            color: #f3f4f6;
          }

          .email-subject-row {
            border-top-color: #374151;
          }

          .email-subject {
            color: #ffffff;
          }

          .email-attachments {
            border-top-color: #374151;
          }

          .email-attachment-chip {
            background: #374151;
            border-color: #4b5563;
            color: #d1d5db;
          }

          .email-attachment-chip:hover {
            background: #4b5563;
          }

          .email-body {
            color: #f3f4f6;
          }
        }

        /* Print styles */
        @media print {
          .email-window {
            box-shadow: none;
            border: 1px solid #e5e7eb;
          }

          .email-window-titlebar {
            background: #f3f4f6;
          }

          .traffic-light {
            display: none;
          }

          .email-window-title {
            justify-content: flex-start;
          }

          .email-window-titlebar-spacer {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}